import React, { useRef, useState, useEffect } from "react"
import { Button, Dropdown, Menu, Popover, Select } from "antd"
import { Icon } from "components"
import Strings from "utils/strings"
import { getStatusLabel as getStatusLabelUtil } from "utils/status"

const { Option } = Select

// Order status values for filtering
export const ORDER_STATUS_VALUES = [
	"address_confirmation",
	"picking",
	"packing",
	"aggregation",
	"loading",
	"ready_to_van",
	"dispatching",
	"on_transit",
	"delivered",
	"not_delivered",
	"cancelled",
	"returned",
	"dispute",
	"billing",
] as const

export type OrderStatus = (typeof ORDER_STATUS_VALUES)[number]

// Quick filter presets
export const STATUS_FILTER_PRESETS = {
	preparation: [
		"address_confirmation",
		"picking",
		"packing",
		"aggregation",
		"loading",
		"ready_to_van",
	] as OrderStatus[],
	inRoute: ["dispatching", "on_transit"] as OrderStatus[],
	concluded: [
		"delivered",
		"not_delivered",
		"cancelled",
		"returned",
		"dispute",
		"billing",
	] as OrderStatus[],
}

interface StatusFilterDropdownProps {
	selectedStatus: OrderStatus[]
	setSelectedStatus: (status: OrderStatus[]) => void
}

const StatusFilterDropdown: React.FC<StatusFilterDropdownProps> = ({
	selectedStatus,
	setSelectedStatus,
}) => {
	// Internal state for immediate UI updates
	const [internalSelection, setInternalSelection] =
		useState<OrderStatus[]>(selectedStatus)

	// Track the status when dropdown opens to compare on close
	const statusOnOpenRef = useRef<OrderStatus[]>([])

	// Sync internal state when external prop changes
	useEffect(() => {
		setInternalSelection(selectedStatus)
	}, [selectedStatus])

	// Helper to check if two status arrays are equal
	const areStatusArraysEqual = (a: OrderStatus[], b: OrderStatus[]) => {
		if (a.length !== b.length) return false
		const sortedA = [...a].sort()
		const sortedB = [...b].sort()
		return sortedA.every((val, idx) => val === sortedB[idx])
	}

	// Commit internal selection to parent and trigger fetch
	const commitSelection = (newSelection: OrderStatus[]) => {
		if (!areStatusArraysEqual(selectedStatus, newSelection)) {
			setSelectedStatus(newSelection)
		}
	}

	// Determine which preset is currently active (if any)
	const getActivePreset = () => {
		const sortedSelected = [...internalSelection].sort()
		for (const [key, preset] of Object.entries(STATUS_FILTER_PRESETS)) {
			const sortedPreset = [...preset].sort()
			if (
				sortedSelected.length === sortedPreset.length &&
				sortedSelected.every((val, idx) => val === sortedPreset[idx])
			) {
				return key
			}
		}
		// Check if all statuses are selected
		if (internalSelection.length === ORDER_STATUS_VALUES.length) {
			return "all"
		}
		return null
	}

	const activePreset = getActivePreset()

	const handlePresetClick = (presetKey: string) => {
		const preset =
			presetKey === "all"
				? [...ORDER_STATUS_VALUES]
				: [
						...STATUS_FILTER_PRESETS[
							presetKey as keyof typeof STATUS_FILTER_PRESETS
						],
					]
		setInternalSelection(preset)
		commitSelection(preset)
	}

	const presetMenu = (
		<Menu>
			<Menu.Item
				key="preparation"
				onClick={() => handlePresetClick("preparation")}
				style={{
					fontWeight: activePreset === "preparation" ? "bold" : "normal",
					backgroundColor:
						activePreset === "preparation" ? "#e6f7ff" : undefined,
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<Icon name="box" style={{ fontSize: 14 }} />
					<span>{Strings.orders.preparation}</span>
				</div>
			</Menu.Item>
			<Menu.Item
				key="inRoute"
				onClick={() => handlePresetClick("inRoute")}
				style={{
					fontWeight: activePreset === "inRoute" ? "bold" : "normal",
					backgroundColor: activePreset === "inRoute" ? "#e6f7ff" : undefined,
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<Icon name="delivery" style={{ fontSize: 14 }} />
					<span>{Strings.orders.inRoute}</span>
				</div>
			</Menu.Item>
			<Menu.Item
				key="concluded"
				onClick={() => handlePresetClick("concluded")}
				style={{
					fontWeight: activePreset === "concluded" ? "bold" : "normal",
					backgroundColor: activePreset === "concluded" ? "#e6f7ff" : undefined,
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<Icon name="correct-symbol" style={{ fontSize: 12 }} />
					<span>{Strings.orders.concluded}</span>
				</div>
			</Menu.Item>
			<Menu.Item
				key="all"
				onClick={() => handlePresetClick("all")}
				style={{
					fontWeight: activePreset === "all" ? "bold" : "normal",
					backgroundColor: activePreset === "all" ? "#e6f7ff" : undefined,
				}}
			>
				<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
					<Icon name="list" style={{ fontSize: 14 }} />
					<span>{Strings.generic.all}</span>
				</div>
			</Menu.Item>
		</Menu>
	)

	// Check if next status button should be shown
	const showNextStatusButton = (() => {
		if (internalSelection.length !== 1) return false
		const currentIndex = ORDER_STATUS_VALUES.indexOf(internalSelection[0])
		return (
			currentIndex !== -1 && currentIndex !== ORDER_STATUS_VALUES.length - 1
		)
	})()

	// Show an arrow that changes status to next status if only one status is selected
	const NextStatusButton = () => {
		const currentIndex = ORDER_STATUS_VALUES.indexOf(internalSelection[0])
		const nextStatus = ORDER_STATUS_VALUES[currentIndex + 1]
		return (
			<Popover content={getStatusLabelUtil(nextStatus)} trigger="hover">
				<Button
					onClick={() => {
						const newSelection = [nextStatus]
						setInternalSelection(newSelection)
						commitSelection(newSelection)
					}}
					style={{
						borderLeft: "1px solid #f0f0f0",
						borderRadius: "0 4px 4px 0",
						padding: "0 8px",
						width: 38,
					}}
				>
					<svg
						width="14"
						height="14"
						viewBox="0 0 14 14"
						fill="none"
						xmlns="http://www.w3.org/2000/svg"
					>
						<path
							d="M10.5 7L1 7M10.5 7L7.5 4M10.5 7L7.5 10"
							stroke="currentColor"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</Button>
			</Popover>
		)
	}

	return (
		<div style={{ display: "flex", alignItems: "center", gap: 0 }}>
			<style>
				{`
					.status-filter-select .ant-select-selector {
						border-top-right-radius: 0 !important;
						border-bottom-right-radius: 0 !important;
					}
					.status-filter-select .ant-select-selection-overflow {
						gap: 2px !important;
					}
					.status-filter-select .ant-select-selection-overflow-item {
						align-self: center !important;
					}
					.status-filter-select .ant-select-selection-overflow-item > span,
					.status-filter-select .ant-select-selection-item {
						padding: 0 !important;
						margin: 0 !important;
						height: auto !important;
						line-height: normal !important;
						background: transparent !important;
						border: none !important;
						max-width: unset !important;
					}
					.status-filter-select .ant-select-selection-item-content {
						margin: 0 !important;
					}
					.status-filter-select .ant-select-selection-item-remove {
						display: none !important;
					}
					.status-filter-button {
						border-top-left-radius: 0 !important;
						border-bottom-left-radius: 0 !important;
						padding: 8px 12px;
					}
					.status-filter-button.no-border-radius {
						border-radius: 0 !important;
					}
				`}
			</style>
			<Select
				mode="multiple"
				placeholder={Strings.orders.filterByStatus}
				value={internalSelection}
				onChange={(values: OrderStatus[]) => {
					const sortedValues = values.sort((a, b) => {
						return (
							ORDER_STATUS_VALUES.indexOf(a) - ORDER_STATUS_VALUES.indexOf(b)
						)
					})
					setInternalSelection(sortedValues)
				}}
				onDropdownVisibleChange={(open) => {
					if (open) {
						// Store current status when dropdown opens
						statusOnOpenRef.current = [...internalSelection]
					} else {
						// Only commit and fetch when dropdown closes if status actually changed
						if (
							!areStatusArraysEqual(statusOnOpenRef.current, internalSelection)
						) {
							commitSelection(internalSelection)
						}
					}
				}}
				onClear={() => {
					setInternalSelection([])
					commitSelection([])
				}}
				allowClear
				className="status-filter-select HeaderStatusFilter"
				maxTagCount="responsive"
				maxTagPlaceholder={(omittedValues) => (
					<span
						style={{
							display: "inline-flex",
							alignItems: "center",
							padding: "0 6px",
							fontSize: 11,
							lineHeight: "20px",
							background: "#f0f0f0",
							borderRadius: 2,
						}}
					>
						+{omittedValues.length}
					</span>
				)}
				listItemHeight={32}
				listHeight={192}
				tagRender={({ label, closable, onClose }) => (
					<span
						style={{
							display: "inline-flex",
							alignItems: "center",
							padding: "0 6px",
							fontSize: 11,
							lineHeight: "20px",
							background: "#f0f0f0",
							borderRadius: 2,
							width: 68,
						}}
					>
						<span
							style={{
								overflow: "hidden",
								textOverflow: "ellipsis",
								whiteSpace: "nowrap",
								marginRight: closable ? "auto" : 0,
							}}
						>
							{label}
						</span>
						{closable && (
							<span
								onClick={onClose}
								style={{ marginLeft: 2, cursor: "pointer", fontSize: 10 }}
							>
								×
							</span>
						)}
					</span>
				)}
			>
				{ORDER_STATUS_VALUES.map((status) => (
					<Option key={status} value={status}>
						{getStatusLabelUtil(status)}
					</Option>
				))}
			</Select>
			<Dropdown overlay={presetMenu} trigger={["click"]}>
				<Button
					className={`status-filter-button ${showNextStatusButton ? "no-border-radius" : ""}`}
					style={{
						display: "flex",
						alignItems: "center",
						width: 38,
						gap: 6,
						backgroundColor: activePreset ? "#e6f7ff" : undefined,
						borderColor: activePreset ? "#1890ff" : undefined,
					}}
				>
					<Icon name="filter" style={{ fontSize: 14 }} />
				</Button>
			</Dropdown>
			{showNextStatusButton && <NextStatusButton />}
		</div>
	)
}

export default StatusFilterDropdown
